import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Search, Skeleton, Tooltip } from '@pmi/dsm-react';
import Overlay from '../../../../shared/components/Overlay';
import UsersList from './UsersList';
import { SortedColumn } from '../../../../shared/components/ResponsiveTable/models';
import { User } from '../../../../shared/models/UserModels';
import { useGetUsersQuery } from './UsersList/apiSlice';
import { accountSelector } from '../../../../shared/store/shared/accountSlice';
import useDebounce from '../../../../shared/hooks/useDebounce';
import styles from '../../../../shared/dsm.module.scss';
import usersStyles from './usersTab.module.scss';
import InviteWizard from '../../../../shared/components/InviteWizard';
import PeopleCardList from '../PeopleCardList';
import UserCardListTemplate from './UserCardListTemplate';
import { UserActions } from './UsersList/models';
import EditModal from './EditModal';
import FilterButton from '../FilterBy/FilterButton';
import FilterUsers, { UserFilters } from './FilterUsers';
import { useGetDealsListQuery } from '../../../../shared/store/shared/dealsApiSlice';
import PERMISSIONS from '../../../../shared/permissions';
import usePermissions from '../../../../shared/hooks/usePermissions';
import useBreakpoint, {
  SIZE_MD,
  SIZE_SM,
  SIZE_XS,
} from '../../../../shared/hooks/useBreakpoint';
import AuthService, {
  IAuthService,
} from '../../../../shared/services/AuthService';
import { getOidcApiRoot } from '../../../../shared/env-vars';
import { saveAsResponse } from '../../../../shared/helpers';
import ViewUserModal from './ViewUserModal';
import { adminUsersLayoutSelector, removeUsers } from './adminUsersLayoutSlice';
import BulkActionMenu from './BulkActionMenu';
import RowSelectionHeader from './UsersList/TableCells/RowSelectionHeader';
import { dealHasExpired } from '../../../../shared/utils/dataHelpers';
import useAdminVisitedPages from '../../../../shared/hooks/useAdminVisitedPages';
import DisableUserModal from './DisableUserModal';

let authService: IAuthService;

export default function UsersTab() {
  const [showInviteWizard, setShowInviteWizard] = useState(false);
  const [editableUser, setEditableUser] = useState<User | undefined>();
  const [disableUser, setDisableUser] = useState<User | undefined>();
  const [restoreUser, setRestoreUser] = useState<User | undefined>();
  const [viewUser, setViewUser] = useState<User | undefined>();
  const { currentAccountId } = useSelector(accountSelector);
  const [sortedColumn, setSortedColumn] = useState<SortedColumn>(null);
  const [sortDirection, setSortDirection] = useState('-');
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDealUsersCsvDownloading, setIsDealUsersCsvDownloading] =
    useState(false);
  const bp = useBreakpoint();
  const dispatch = useDispatch();
  const { selectedRows } = useSelector(adminUsersLayoutSelector);
  const [filtered, setFiltered] = useState<{
    isFiltered: boolean;
    selectedFilters: UserFilters;
  }>({
    isFiltered: false,
    selectedFilters: {
      registrationStatus: [],
      products: [],
      dealIds: [],
    },
  });
  const [resetFilter, setResetFilter] = useState(false);
  const { hasAccess, isLoading: permissionsLoading } = usePermissions();
  const debouncedSearch = useDebounce(searchText, 1000);
  const userTableRef = useRef(null);
  const {
    data: rawUsersData,
    isLoading: processing,
    isFetching: usersFetching,
    refetch,
  } = useGetUsersQuery({
    accountId: currentAccountId,
    page: currentPage,
    pageSize,
    keyword: debouncedSearch,
    dealIds: filtered.selectedFilters.dealIds,
    statuses: filtered.selectedFilters.registrationStatus,
    productSkus: filtered.selectedFilters.products,
    sortOrder: sortDirection,
    sortField: sortedColumn ? sortedColumn.name : 'registration',
  });
  const totalItemCount = rawUsersData?.totalItemCount;

  const userList: User[] = useMemo(
    () => rawUsersData?.items?.slice() ?? [],
    [rawUsersData],
  );

  useEffect(() => {
    if (rawUsersData?.totalItemCount) {
      const count = Math.ceil(rawUsersData.totalItemCount / pageSize);
      setTotalPages((prev) => (prev === count ? prev : count));
    }
  }, [rawUsersData?.totalItemCount, pageSize]);

  useEffect(() => {
    if (!searchLoading) return undefined;
    // TODOs: remove this once search API is fully working
    const timer = setTimeout(() => setSearchLoading(false), 100);
    return () => clearTimeout(timer);
  }, [searchLoading, rawUsersData]);

  const {
    data: dealList,
    isLoading: isDealListLoading,
    isFetching: isDealListFetching,
  } = useGetDealsListQuery(currentAccountId);

  const accessibleDeals = useMemo(() => {
    const hasPermission = hasAccess({
      permissions: [PERMISSIONS.ManageDealUsers],
    });
    return hasPermission ? dealList ?? [] : [];
  }, [isDealListLoading, isDealListFetching, dealList]);

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    userTableRef.current.scrollIntoView();
    dispatch(removeUsers([]));
  };
  const handleSort = (column: SortedColumn) => {
    setSortedColumn(column);
    setSortDirection(column.direction === 'Desc' ? '-' : '+');
    setTimeout(() => {
      setCurrentPage(1);
      refetch();
    }, 100);
  };

  const onPageSizeChange = (size: number) => {
    setCurrentPage(1);
    setPageSize(size);
    setTotalPages(1);
  };

  const onSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchText(e.currentTarget.value);
    setCurrentPage(1);
    setSearchLoading(true);
  };

  const onSearchClear = () => {
    setSearchText('');
    setSearchLoading(true);
  };

  const actions: UserActions = {
    onEdit: setEditableUser,
    onRemove: setRestoreUser,
    onView: setViewUser,
  };
  const cardTemplate = (item: User, isInternalAdmin: boolean) => (
    <UserCardListTemplate
      actions={actions}
      item={item}
      isInternalAdmin={isInternalAdmin}
    />
  );

  const onFilterChange = (
    isFiltered: boolean,
    selectedFilters: UserFilters,
  ) => {
    setResetFilter(false);
    setFiltered({
      isFiltered,
      selectedFilters,
    });
    setCurrentPage(1);
  };

  const isLoading = processing || permissionsLoading || searchLoading;
  const noData =
    !isLoading &&
    searchText?.length === 0 &&
    !filtered?.isFiltered &&
    userList?.length === 0;
  const isFilterMode =
    userList?.length === 0 && searchText?.length === 0 && filtered?.isFiltered;
  const isSearchMode = userList?.length === 0 && searchText?.length > 0;

  const renderTable = () => (
    <>
      <UsersList
        list={userList}
        isLoading={isLoading}
        handleSort={handleSort}
        page={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSize={pageSize}
        actions={actions}
        accessibleDeals={accessibleDeals}
      />
      {noData && (
        <div
          className={`d-block my-5 text-center user-list-data mx-auto w-75 ${usersStyles.noResult}`}
        >
          No Data to Display...
        </div>
      )}
      {isFilterMode && (
        <div
          className={`d-block my-5 text-center mx-auto w-75 user-list-data no-users-found ${usersStyles.noResult}`}
        >
          There are no users found.
        </div>
      )}
      {isSearchMode && (
        <div
          className={`d-block my-5 text-center mx-auto w-75 user-list-data ${usersStyles.noResult}`}
        >
          There are no search results matching{' '}
          <span data-exclude-lg>
            &ldquo;<b>{searchText}</b>&rdquo;.
          </span>
          <br />
          Please check your spelling or try another search.
        </div>
      )}
    </>
  );
  const renderCardList = () => (
    <>
      <PeopleCardList<User>
        cardTemplate={cardTemplate}
        items={userList}
        paginationProps={{
          showPagination: userList?.length > 1,
          page: currentPage,
          totalPages,
          onPageChange,
          pageSize,
        }}
      />
      {noData && (
        <div
          className={`d-block my-5 text-center mx-auto w-75 ${usersStyles.noResult}`}
        >
          No Data to Display...
        </div>
      )}
      {isFilterMode && (
        <div
          className={`d-block my-5 text-center mx-auto w-75 ${usersStyles.noResult}`}
        >
          There are no users found.
        </div>
      )}
      {isSearchMode && (
        <div
          className={`d-block my-5 text-center mx-auto w-75 ${usersStyles.noResult}`}
        >
          There are no search results matching{' '}
          <span data-exclude-lg>
            &ldquo;<b>{searchText}</b>&rdquo;.
          </span>
          <br />
          Please check your spelling or try another search.
        </div>
      )}
    </>
  );

  const renderUserCount = () => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = (currentPage - 1) * pageSize + pageSize;
    const selectedUserIds = selectedRows.map((e) => e.id);
    const selectedUsers =
      userList?.filter((user) => selectedUserIds.includes(user.id))?.length ??
      0;
    if (!totalItemCount) return null;

    return (
      <div className={`pb-2 ${usersStyles.tableResultCounts}`}>
        {usersFetching && <>Loading...</>}
        {!usersFetching && (
          <>
            Displaying {start}-{totalItemCount < end ? totalItemCount : end} of{' '}
            {totalItemCount} {totalItemCount > 1 ? 'users' : 'user'}
          </>
        )}
        {selectedUsers > 0 && <>, {selectedUsers} selected</>}
      </div>
    );
  };

  const downloadDealUsersCsv = async () => {
    if (!authService) {
      authService = new AuthService();
    }
    try {
      setIsDealUsersCsvDownloading(true);
      const response = await fetch(
        `${getOidcApiRoot()}/api/accounts/${currentAccountId}/reports/csv`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'text/csv',
            Authorization: `Bearer ${
              (
                await authService.getUser()
              ).access_token
            }`,
            'X-SelectedAccountId': currentAccountId,
          },
        },
      );

      if (!response.ok) {
        return;
      }
      saveAsResponse(response, null);
      setIsDealUsersCsvDownloading(false);
    } catch (error) {
      console.error('Failed to download file', error);
      setIsDealUsersCsvDownloading(false);
    }
  };

  const getButtonSize = () => {
    if (bp === SIZE_SM || bp === SIZE_MD) {
      return 'sm';
    }
    if (bp === SIZE_XS) {
      return 'xs';
    }
    return 'md';
  };

  const ableToAddUser = useMemo(() => {
    if (dealList && dealList.length > 0) {
      return dealList.filter((d) => !dealHasExpired(d))?.length === 0;
    }
    return false;
  }, [dealList]);

  const visits = useAdminVisitedPages('people.users');

  useEffect(() => {
    if (!isDealListLoading && !isLoading && !visits.isLoading) {
      visits.performFirstTimeVisit('.user-list-data');
    }
  }, [isDealListLoading, isLoading, visits.isLoading]);
  useEffect(() => {
    refetch();
  }, [debouncedSearch, filtered.isFiltered, currentPage]);

  return (
    <div
      className={usersStyles.people__listTabCard_usersTab}
      ref={userTableRef}
    >
      <div className="d-flex flex-column flex-sm-row my-3 w-100">
        <div className="people-users_search-filter d-sm-flex d-block">
          <Search
            id="userSearch"
            placeholder="Search by name or email address"
            value={searchText}
            onClear={onSearchClear}
            onChange={onSearchChange}
            className={` ${styles.secondarySearch} me-0 me-sm-3 ${usersStyles.searchUserTextBox}`}
            style={{ width: 330 }}
          />
          <div className="d-flex">
            <FilterButton onOpen={(open) => setIsFilterOpen(open)} />
            <BulkActionMenu userList={userList} />
          </div>
        </div>

        <div className="flex-grow-1 d-flex justify-content-sm-end justify-content-start">
          <div className="d-flex">
            <div className="d-flex">
              <Button
                titleText="Download .csv"
                size={getButtonSize()}
                className="people-users_download_csv-btn me-2 me-sm-3"
                variant="outline-secondary"
                disabled={isDealUsersCsvDownloading}
                onClick={() => downloadDealUsersCsv()}
              />
              {isDealListFetching || isDealListLoading ? (
                <Skeleton className="mw-100" width="120" height={50} />
              ) : (
                <Tooltip
                  content="You cannot add people to an expired Agreement."
                  type="sm"
                  blockTriggers={!ableToAddUser}
                  placement="top"
                  className="darkTooltip"
                >
                  <Button
                    className="people-users_add_people-btn"
                    titleText="Add People"
                    size={getButtonSize()}
                    onClick={() => setShowInviteWizard(true)}
                    disabled={ableToAddUser}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>

      {!processing && (
        <FilterUsers
          show={isFilterOpen}
          onFilter={onFilterChange}
          reFetchData={!showInviteWizard}
          resetFilter={resetFilter}
        />
      )}
      <div className="d-flex">
        <div className="d-block flex-grow-1">{renderUserCount()}</div>
        <div className="d-sm-none d-block pe-3">
          <RowSelectionHeader users={userList} />
        </div>
      </div>

      {bp !== SIZE_XS && renderTable()}
      {bp === SIZE_XS && renderCardList()}
      {viewUser && (
        <ViewUserModal
          onAddProduct={setEditableUser}
          onRestore={setRestoreUser}
          onDisable={setDisableUser}
          user={userList?.find((u) => u.id === viewUser.id) || viewUser}
          onCancel={() => {
            setViewUser(undefined);
          }}
        />
      )}
      {editableUser && (
        <EditModal
          editMode="full"
          user={userList?.find((u) => u.id === editableUser.id) || editableUser}
          onCancel={() => setEditableUser(undefined)}
        />
      )}
      {restoreUser && (
        <EditModal
          editMode="restore"
          user={userList?.find((u) => u.id === restoreUser.id) || restoreUser}
          onCancel={() => setRestoreUser(undefined)}
        />
      )}
      {disableUser && (
        <DisableUserModal
          user={disableUser}
          onCancel={() => {
            setDisableUser(undefined);
          }}
          onSuccess={() => {
            if (viewUser) {
              setResetFilter(true);
              setSearchText(
                viewUser.email ? viewUser.email : viewUser.personName,
              );
              setCurrentPage(1);
              setSearchLoading(true);
            }
          }}
        />
      )}
      {showInviteWizard && (
        <InviteWizard
          onCancel={() => setShowInviteWizard(false)}
          singleAgreement={dealList.length === 1}
        />
      )}
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={isLoading || visits.isLoading}
      />
    </div>
  );
}
